/**
 * AuctionVehicleSource — Jun-2026 (User-Wunsch).
 *
 * Bei AUKTIONEN werden die Eckdaten-Felder (Titel/Marke/Modell/Erstzulassung/
 * Standort/Bilder) ausgeblendet, weil die Fahrzeugdaten bereits aus einem
 * bestehenden Fahrzeug-Inserat stammen. Diese Karte lässt den Händler ein
 * Bestandsfahrzeug auswählen (a) bzw. zeigt bei bereits vorhandenen Daten eine
 * kompakte Zusammenfassung (b — Bearbeiten/Konvertieren). Ein optionaler
 * „manuell anlegen"-Schalter blendet die Eckdaten-Eingabe als Fallback ein.
 */
import { useEffect, useMemo, useState } from "react";
import { Car, Loader2, X, PencilLine, Search, AlertTriangle, Ban } from "lucide-react";
import { toast } from "sonner";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import api from "../../lib/api";
import { normalizeImageUrl } from "../../lib/imageUrl";

const SALE_LABEL = { fixed: "Festpreis-Inserat", fixed_price: "Festpreis-Inserat", auction: "Auktion", rental: "Vermietung", pawn: "Pfand / Refinanzieren" };

export default function AuctionVehicleSource({
    userId,
    form,
    mode = "auction",
    selectedId,
    onSelect,
    onClear,
    manualEntry,
    onToggleManual,
}) {
    const ctxLabel = mode === "rental" ? "Vermietung" : mode === "pawn" ? "Pfand / Refinanzieren" : "Auktion";
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(Boolean(userId));
    const [query, setQuery] = useState("");
    const [focused, setFocused] = useState(false);
    const [dupes, setDupes] = useState([]);

    useEffect(() => {
        let active = true;
        if (!userId) return undefined;
        api.get(`/listings?owner_id=${userId}`)
            .then((r) => {
                if (!active) return;
                const list = (r.data || []).filter(
                    (l) => !["auction", "rental", "pawn"].includes(l.sale_type) && !l.sold && (l.brand || l.model),
                );
                setVehicles(list);
                setLoading(false);
            })
            .catch(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [userId]);

    const dataPresent = Boolean(form.brand && form.model);
    const showSummary = !manualEntry && (Boolean(selectedId) || dataPresent);
    const img = normalizeImageUrl(form.images?.[0]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const recency = (v) => String(v.updated_at || v.created_at || "");
        if (!q) {
            return [...vehicles]
                .sort((a, b) => recency(b).localeCompare(recency(a)))
                .slice(0, 25);
        }
        return vehicles
            .filter((v) =>
                `${v.brand || ""} ${v.model || ""}`.toLowerCase().includes(q) ||
                String(v.internal_number || "").toLowerCase().includes(q) ||
                String(v.title || "").toLowerCase().includes(q) ||
                String(v.vin || "").toLowerCase().includes(q) ||
                String(v.location || "").toLowerCase().includes(q),
            )
            .slice(0, 25);
    }, [query, vehicles]);

    const pick = async (v) => {
        onSelect(v);
        setQuery("");
        setFocused(false);
        try {
            const { data } = await api.get("/listings/duplicate-check", { params: { vin: v.vin || "", listing_id: v.id } });
            setDupes(data.duplicates || []);
        } catch { setDupes([]); }
    };

    const stopOffer = async (d) => {
        try {
            await api.post(`/listings/${d.id}/pause`);
            setDupes((prev) => prev.filter((x) => x.id !== d.id));
            toast.success(`${SALE_LABEL[d.sale_type] || "Inserat"} gestoppt (offline).`);
        } catch { toast.error("Inserat konnte nicht gestoppt werden."); }
    };

    const clearAll = () => { setDupes([]); onClear(); };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6 space-y-4" data-testid="auction-vehicle-source">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-base font-bold text-[#111827] flex items-center gap-2">
                    <Car className="w-4 h-4" /> Fahrzeug der {ctxLabel}
                </h2>
                <button
                    type="button"
                    onClick={onToggleManual}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827]"
                    data-testid="auction-manual-toggle"
                >
                    <PencilLine className="w-3.5 h-3.5" /> {manualEntry ? "Bestandsfahrzeug wählen" : "Manuell anlegen"}
                </button>
            </div>

            {manualEntry ? (
                <p className="text-sm text-gray-500" data-testid="auction-manual-hint">
                    Manuelle Eingabe aktiv — die Eckdaten-Felder unten ausfüllen.
                </p>
            ) : showSummary ? (
                <div className="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="auction-vehicle-summary">
                    {img ? (
                        <img src={img} alt="" className="w-20 h-16 object-cover rounded-sm shrink-0" />
                    ) : (
                        <div className="w-20 h-16 bg-gray-200 rounded-sm shrink-0 flex items-center justify-center"><Car className="w-6 h-6 text-gray-400" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="font-bold text-[#111827] truncate" data-testid="auction-vehicle-title">
                            {form.title || `${form.brand} ${form.model}`.trim()}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                            {[form.brand, form.model, form.year, form.location].filter(Boolean).join(" · ") || "—"}
                            {Array.isArray(form.images) && form.images.length > 0 ? ` · ${form.images.length} Bild(er)` : ""}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={clearAll}
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:text-red-600 shrink-0"
                        data-testid="auction-vehicle-change"
                    >
                        <X className="w-3.5 h-3.5" /> Ändern
                    </button>
                </div>
            ) : (
                <div data-testid="auction-vehicle-picker">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fahrzeug aus meinem Bestand wählen *</Label>
                    {loading ? (
                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Bestand wird geladen…</div>
                    ) : vehicles.length === 0 ? (
                        <p className="mt-2 text-sm text-gray-500" data-testid="auction-no-vehicles">
                            Kein passendes Bestandsfahrzeug gefunden. Lege das Fahrzeug zuerst als Inserat an oder nutze &bdquo;Manuell anlegen&ldquo;.
                        </p>
                    ) : (
                        <div className="relative mt-1.5">
                            <div className="relative">
                                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <Input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onFocus={() => setFocused(true)}
                                    onBlur={() => setTimeout(() => setFocused(false), 150)}
                                    placeholder="Marke, Modell, Angebotsnummer, FIN oder Standort suchen…"
                                    className="pl-9 rounded-sm border-2 border-red-400 h-10"
                                    data-testid="auction-vehicle-search"
                                />
                            </div>
                            {(focused || query) && (
                                <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-200 rounded-sm shadow-lg" data-testid="auction-vehicle-results">
                                    {filtered.length === 0 ? (
                                        <div className="px-3 py-3 text-sm text-gray-500" data-testid="auction-vehicle-noresult">Kein Treffer für &bdquo;{query}&ldquo;.</div>
                                    ) : (
                                        <>
                                            {!query && (
                                                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-400 bg-gray-50 border-b border-gray-100" data-testid="auction-vehicle-recent-header">Zuletzt genutzt</div>
                                            )}
                                            {filtered.map((v) => (
                                            <button
                                                key={v.id}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => pick(v)}
                                                className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between gap-3"
                                                data-testid={`auction-vehicle-option-${v.id}`}
                                            >
                                                <span className="min-w-0 truncate">
                                                    <span className="font-semibold text-[#111827]">{`${v.brand || ""} ${v.model || ""}`.trim() || "Fahrzeug"}</span>
                                                    {v.year ? <span className="text-gray-500"> · {v.year}</span> : null}
                                                    {v.location ? <span className="text-gray-400"> · {v.location}</span> : null}
                                                </span>
                                                {v.internal_number && (
                                                    <span className="shrink-0 text-[11px] font-mono-num font-bold bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm">{v.internal_number}</span>
                                                )}
                                            </button>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                            <p className="mt-1.5 text-[11px] text-gray-400">Tipp: Du kannst auch direkt die Angebots-/Fahrzeugangebotsnummer eingeben.</p>
                        </div>
                    )}
                </div>
            )}

            {dupes.length > 0 && (
                <div className="border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="auction-duplicate-warning">
                    <div className="flex items-center gap-2 text-amber-800 font-bold text-sm mb-1">
                        <AlertTriangle className="w-4 h-4" /> Dieses Fahrzeug ist bereits online
                    </div>
                    <p className="text-xs text-amber-700 mb-2.5">
                        Möchtest du das bestehende Inserat stoppen, um ein Doppel-Inserat desselben Fahrzeugs zu vermeiden?
                    </p>
                    {dupes.map((d) => (
                        <div key={d.id} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-sm px-3 py-2 mb-1.5 last:mb-0" data-testid={`auction-duplicate-${d.id}`}>
                            <span className="text-sm min-w-0 truncate">
                                <span className="font-bold">{SALE_LABEL[d.sale_type] || "Inserat"}</span>
                                {d.internal_number ? <span className="text-gray-500"> · {d.internal_number}</span> : null}
                                {d.title ? <span className="text-gray-500"> · {d.title}</span> : null}
                            </span>
                            <Button
                                type="button"
                                onClick={() => stopOffer(d)}
                                variant="outline"
                                className="rounded-sm h-8 text-xs border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
                                data-testid={`auction-duplicate-stop-${d.id}`}
                            >
                                <Ban className="w-3.5 h-3.5 mr-1.5" /> Stoppen
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
